(() => {
  if (customElements.get('lc-salome-video-shop')) return;

  class LCSalomeVideoShop extends HTMLElement {
    constructor() {
      super();
      this.currentIndex = 0;
      this.timer = null;
      this.scrollFrame = null;
      this.isSectionVisible = false;
      this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.mobileViewport = window.matchMedia('(max-width: 749px)');
    }

    connectedCallback() {
      this.viewport = this.querySelector('[data-lc-salome-viewport]');
      this.slides = [...this.querySelectorAll('[data-lc-salome-slide]')];
      this.videos = [...this.querySelectorAll('[data-lc-salome-video]')];
      this.dots = [...this.querySelectorAll('[data-lc-salome-dot]')];
      this.previousButton = this.querySelector('[data-lc-salome-previous]');
      this.nextButton = this.querySelector('[data-lc-salome-next]');
      this.status = this.querySelector('[data-lc-salome-status]');
      this.interval = Math.max(4, Number(this.dataset.autoplaySeconds) || 5) * 1000;
      this.videoHandlers = [];

      this.videos.forEach((video) => {
        video.muted = true;
        const button = video.parentElement.querySelector('[data-lc-salome-toggle]');
        if (!button) return;

        const handler = () => this.toggleVideo(video, button);
        button.addEventListener('click', handler);
        this.videoHandlers.push({ button, handler });
        this.syncButton(video, button);
      });

      this.previousButton?.addEventListener('click', this.showPrevious);
      this.nextButton?.addEventListener('click', this.showNext);
      this.dots.forEach((dot) => dot.addEventListener('click', this.handleDotClick));
      this.viewport?.addEventListener('scroll', this.handleScroll, { passive: true });
      this.viewport?.addEventListener('touchstart', this.stopAutoplay, { passive: true });
      this.viewport?.addEventListener('touchend', this.handleTouchEnd, { passive: true });
      this.addEventListener('mouseenter', this.stopAutoplay);
      this.addEventListener('mouseleave', this.startAutoplay);
      this.addEventListener('focusin', this.stopAutoplay);
      this.addEventListener('focusout', this.handleFocusOut);
      document.addEventListener('visibilitychange', this.handleVisibilityChange);
      this.reduceMotion.addEventListener('change', this.handlePreferenceChange);
      this.mobileViewport.addEventListener('change', this.handleViewportChange);

      this.sectionObserver = new IntersectionObserver(this.handleIntersection, { threshold: [0, 0.25, 0.6] });
      this.sectionObserver.observe(this);

      this.updateCurrent(0);
      this.handleViewportChange();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      if (this.scrollFrame) cancelAnimationFrame(this.scrollFrame);
      this.sectionObserver?.disconnect();
      this.videoHandlers.forEach(({ button, handler }) => button.removeEventListener('click', handler));
      this.previousButton?.removeEventListener('click', this.showPrevious);
      this.nextButton?.removeEventListener('click', this.showNext);
      this.dots.forEach((dot) => dot.removeEventListener('click', this.handleDotClick));
      this.viewport?.removeEventListener('scroll', this.handleScroll);
      this.viewport?.removeEventListener('touchstart', this.stopAutoplay);
      this.viewport?.removeEventListener('touchend', this.handleTouchEnd);
      this.removeEventListener('mouseenter', this.stopAutoplay);
      this.removeEventListener('mouseleave', this.startAutoplay);
      this.removeEventListener('focusin', this.stopAutoplay);
      this.removeEventListener('focusout', this.handleFocusOut);
      document.removeEventListener('visibilitychange', this.handleVisibilityChange);
      this.reduceMotion.removeEventListener('change', this.handlePreferenceChange);
      this.mobileViewport.removeEventListener('change', this.handleViewportChange);
    }

    updateCurrent(index) {
      if (!this.slides.length) return;

      this.currentIndex = (index + this.slides.length) % this.slides.length;
      this.dots.forEach((dot, dotIndex) => {
        const isActive = dotIndex === this.currentIndex;
        dot.classList.toggle('is-active', isActive);
        dot.setAttribute('aria-current', String(isActive));
      });

      if (this.status) {
        this.status.textContent = (this.dataset.statusLabel || '%index% / %count%')
          .replace('%index%',String(this.currentIndex + 1))
          .replace('%count%',String(this.slides.length));
      }
      this.syncPlayback();
    }

    showSlide(index, restart = true, behavior = 'smooth') {
      if (!this.mobileViewport.matches || !this.viewport || !this.slides.length) return;

      const nextIndex = (index + this.slides.length) % this.slides.length;
      const nextSlide = this.slides[nextIndex];
      this.updateCurrent(nextIndex);
      this.viewport.scrollTo({ left: nextSlide.offsetLeft, behavior: this.reduceMotion.matches ? 'auto' : behavior });
      if (restart) this.startAutoplay();
    }

    showPrevious = () => this.showSlide(this.currentIndex - 1);

    showNext = () => this.showSlide(this.currentIndex + 1);

    handleDotClick = (event) => {
      this.showSlide(Number(event.currentTarget.dataset.lcSalomeDot));
    };

    handleScroll = () => {
      if (!this.mobileViewport.matches || this.scrollFrame) return;

      this.scrollFrame = requestAnimationFrame(() => {
        this.scrollFrame = null;
        const closestIndex = this.slides.reduce((closest, slide, index) => {
          const distance = Math.abs(slide.offsetLeft - this.viewport.scrollLeft);
          return distance < closest.distance ? { index, distance } : closest;
        }, { index: 0, distance: Number.POSITIVE_INFINITY }).index;

        if (closestIndex !== this.currentIndex) this.updateCurrent(closestIndex);
      });
    };

    handleTouchEnd = () => {
      window.setTimeout(this.startAutoplay, 350);
    };

    handleFocusOut = (event) => {
      if (!this.contains(event.relatedTarget)) this.startAutoplay();
    };

    handleIntersection = (entries) => {
      const entry = entries[entries.length - 1];
      this.isSectionVisible = entry.isIntersecting && entry.intersectionRatio >= 0.25;
      this.syncPlayback();
      if (this.isSectionVisible) this.startAutoplay();
      else this.stopAutoplay();
    };

    handleVisibilityChange = () => {
      this.syncPlayback();
      if (document.hidden) this.stopAutoplay();
      else this.startAutoplay();
    };

    handlePreferenceChange = () => {
      this.syncPlayback();
      if (this.reduceMotion.matches) this.stopAutoplay();
      else this.startAutoplay();
    };

    handleViewportChange = () => {
      this.stopAutoplay();
      if (!this.mobileViewport.matches) {
        this.viewport?.scrollTo({ left: 0, behavior: 'auto' });
        this.updateCurrent(0);
      } else {
        this.showSlide(this.currentIndex, false, 'auto');
      }
      this.syncPlayback();
      this.startAutoplay();
    };

    toggleVideo(video, button) {
      if (video.paused) {
        video.dataset.userPaused = 'false';
        video.play().then(() => this.syncButton(video, button)).catch(() => this.syncButton(video, button));
        this.startAutoplay();
      } else {
        video.dataset.userPaused = 'true';
        video.pause();
        this.syncButton(video, button);
        this.stopAutoplay();
      }
    }

    syncPlayback() {
      const canPlay = this.isSectionVisible && !this.reduceMotion.matches && !document.hidden;

      this.videos.forEach((video, index) => {
        const button = video.parentElement.querySelector('[data-lc-salome-toggle]');
        const isCurrent = !this.mobileViewport.matches || index === this.currentIndex;
        const shouldPlay = canPlay && isCurrent && video.dataset.userPaused !== 'true';

        if (shouldPlay) {
          video.play().then(() => this.syncButton(video, button)).catch(() => this.syncButton(video, button));
        } else {
          video.pause();
          this.syncButton(video, button);
        }
      });
    }

    syncButton(video, button) {
      if (!button) return;

      button.classList.toggle('is-paused', video.paused);
      button.setAttribute('aria-label', video.paused ? this.dataset.playLabel : this.dataset.pauseLabel);
    }

    startAutoplay = () => {
      this.stopAutoplay();
      if (
        !this.mobileViewport.matches ||
        !this.isSectionVisible ||
        this.reduceMotion.matches ||
        document.hidden ||
        this.slides.length < 2
      ) return;

      const activeVideo = this.videos[this.currentIndex];
      if (activeVideo?.dataset.userPaused === 'true') return;
      this.timer = window.setInterval(() => this.showSlide(this.currentIndex + 1, false), this.interval);
    };

    stopAutoplay = () => {
      window.clearInterval(this.timer);
      this.timer = null;
    };
  }

  customElements.define('lc-salome-video-shop', LCSalomeVideoShop);
})();
